import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { round_ex } from "@datanac/datanac-api-toolkit/dist/utility/Math";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import Slide from "@mui/material/Slide";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AppContext } from "App";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import { enqueueSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { ArrowDownIcon, ArrowLeftIcon, DeleteIcon } from "../Icons/Icons";

const UNIT_OF_MEASURE_NAMES = ["gal", "oz", "lb", "kg", "ton", "each", "bag"];

const getRowWithEmptyCheck = (rows, fieldName, category, checkKeys = ["name", "quantity", "unit_value"]) => {
  const filtered = (rows || []).filter((row) => row?.line_item_name === fieldName && row?.category === category);
  return filtered.find((row) => checkKeys.every((key) => !row[key]));
};

const isRowEmpty = (row) => !row?.name && Number(row?.quantity) === 0 && Number(row?.unit_value) === 0;

export const BudgetSchedulePopover = ({
  isPopoverOpen,
  anchorEl,
  handlePopoverClose,
  budgetSchedule = [],
  onChange,
  selectedField,
  budgetCountyPracticeId,
}) => {
  const { globalState } = useContext(AppContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [isSkeletonLoading, setIsSkeletonLoading] = useState(true);
  const [localSchedule, setLocalSchedule] = useState([]);

  const [requestQueue, setRequestQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newRowIds, setNewRowIds] = useState([]);

  useEffect(() => {
    setLocalSchedule(budgetSchedule || []);
    setNewRowIds([]);
  }, [budgetSchedule]);

  const processQueue = useCallback(async () => {
    if (isProcessing || requestQueue.length === 0) return;

    setIsProcessing(true);
    const requestsToProcess = [...requestQueue];
    setRequestQueue([]);

    const consolidatedRequest = { added: {}, updated: {}, deleted: {} };

    requestsToProcess.forEach((req) => {
      const id = req.data.id;
      if (req.action === "add") {
        consolidatedRequest.added[id] = req.data;
        delete consolidatedRequest.updated[id];
        delete consolidatedRequest.deleted[id];
      } else if (req.action === "update") {
        if (consolidatedRequest.added[id]) {
          consolidatedRequest.added[id] = req.data;
        } else {
          consolidatedRequest.updated[id] = req.data;
        }
        delete consolidatedRequest.deleted[id];
      } else if (req.action === "delete") {
        if (consolidatedRequest.added[id]) {
          delete consolidatedRequest.added[id];
        } else {
          consolidatedRequest.deleted[id] = req.data;
          delete consolidatedRequest.updated[id];
        }
      }
    });

    const addedItems = Object.values(consolidatedRequest.added).filter((row) => !isRowEmpty(row));

    const finalRequest = [
      ...addedItems,
      ...Object.values(consolidatedRequest.updated),
      ...Object.values(consolidatedRequest.deleted).map((item) => ({ ...item, is_active: false })),
    ];

    try {
      if (finalRequest.length > 0 && typeof onChange === "function") {
        await onChange(finalRequest, false);
      }
    } catch (error) {
      console.error("Error processing queue:", error);
      enqueueSnackbar("Failed to save changes", { variant: "error" });
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, requestQueue, onChange]);

  useEffect(() => {
    const timer = setInterval(() => {
      processQueue();
    }, 2000);

    return () => clearInterval(timer);
  }, [processQueue]);

  const addToQueue = (action, data) => {
    setRequestQueue((prevQueue) => [...prevQueue, { action, data }]);
  };

  const handleBudgetCountySchedule = async (e, row) => {
    const name = e.target.name;
    const value = e.target.value;

    const updatedSchedule = localSchedule.map((item) =>
      item.id === row.id
        ? {
            ...item,
            [name]: value,
            value:
              name === "quantity" || name === "unit_value"
                ? (name === "quantity" ? value : row.quantity) * (name === "unit_value" ? value : row.unit_value)
                : row.value,
          }
        : item
    );
    setLocalSchedule(updatedSchedule);
    const updatedRow = updatedSchedule.find((item) => item.id === row.id);
    addToQueue("update", updatedRow);
  };

  const handleDeleteBudgetCountySchedule = async (row) => {
    const updatedSchedule = localSchedule.filter((item) => item.id !== row.id);
    setLocalSchedule(updatedSchedule);
    addToQueue("delete", row);
    if (detailRowId === row.id) {
      setDetailRowId(null);
    }
  };

  const handleAddNewRecord = () => {
    const emptyRow = getRowWithEmptyCheck(localSchedule, selectedField?.name, selectedField?.category);
    if (emptyRow) {
      enqueueSnackbar("Please fill in at least one field: name, quantity, or unit value", {
        preventDuplicate: true,
        variant: "warning",
      });
      return;
    }

    const currentInputSharePercent = localSchedule.length > 0 ? localSchedule[0]?.input_share_percent : null;

    const newRecord = {
      id: uuidv4(),
      name: "",
      quantity: 0,
      unit_of_measure: "gal",
      unit_value: 0,
      producer_token: globalState?.producer_token,
      budget_county_practice_id: budgetCountyPracticeId,
      category: selectedField?.category,
      ordinal: 0,
      case_quantity: 0,
      case_unit_of_measure: "gal",
      value: 0,
      is_active: true,
      line_item_name: selectedField?.name,
      input_share_percent: currentInputSharePercent,
    };
    setLocalSchedule((prev) => [...prev, newRecord]);
    setNewRowIds((prev) => [...prev, newRecord.id]);
    addToQueue("add", newRecord);
    if (detailRowId) {
      setDetailRowId(newRecord.id);
    }
  };

  const resetLocalState = useCallback(() => {
    setRequestQueue([]);
    setIsProcessing(false);
    setDetailRowId(null);
    setIsSkeletonLoading(true);
    setLocalSchedule(budgetSchedule || []);
  }, [budgetSchedule]);

  const handleClose = async () => {
    await processQueue();
    setNewRowIds([]);
    const finalLocalSchedule = localSchedule.filter((row) => {
      const isNew = !budgetSchedule.some((originalRow) => originalRow.id === row.id);
      if (isNew && isRowEmpty(row)) return false;
      return true;
    });

    if (typeof handlePopoverClose === "function") handlePopoverClose(finalLocalSchedule);
    resetLocalState();
  };

  const filteredBudgetSchedule = useMemo(() => {
    return (localSchedule || [])
      .filter(
        (row) =>
          row.line_item_name === selectedField?.name &&
          row.category === selectedField?.category &&
          row.is_active !== false
      )
      .sort((a, b) => {
        if (!a?.id) return 1;
        if (!b?.id) return -1;
        return new Date(a.createdate) - new Date(b.createdate);
      });
  }, [localSchedule, selectedField]);

  const totalQty = useMemo(
    () => filteredBudgetSchedule.reduce((sum, row) => sum + Number(row.quantity || 0), 0),
    [filteredBudgetSchedule]
  );

  const totalSubtotal = useMemo(
    () => filteredBudgetSchedule.reduce((sum, row) => sum + Number(row.quantity || 0) * Number(row.unit_value || 0), 0),
    [filteredBudgetSchedule]
  );

  useEffect(() => {
    if (isPopoverOpen) {
      setIsSkeletonLoading(true);
      const t = setTimeout(() => setIsSkeletonLoading(false), 500);
      return () => clearTimeout(t);
    }

    // if popover was closed externally, make sure local state is reset
    if (!isPopoverOpen) {
      resetLocalState();
    }
  }, [isPopoverOpen, resetLocalState]);

  // mobile detail view state

  const [detailRowId, setDetailRowId] = useState(null);

  const AddButton = useCallback(() => {
    return (
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleAddNewRecord}
        disabled={isSkeletonLoading}
        sx={{
          padding: "8px 16px",
        }}
      >
        {detailRowId ? "Add More Items" : "Add Item"}
      </Button>
    );
  }, [handleAddNewRecord, isSkeletonLoading]);

  const CloseButton = useCallback(
    () => (
      <Button color="secondary" onClick={handleClose}>
        Close
      </Button>
    ),
    [handleClose]
  );

  const DeleteButton = (row) => (
    <IconButton className="delete-row" color="error" onClick={() => handleDeleteBudgetCountySchedule(row)}>
      <DeleteIcon sx={{ color: "error.main" }} />
    </IconButton>
  );

  const renderDesktopView = () => (
    <>
      <TableRow>
        <TableCell colSpan={1} sx={{ fontWeight: "bold" }}>
          Total:
        </TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>{totalQty}</TableCell>
        <TableCell colSpan={2}></TableCell>
        <TableCell data-testid="schedule-total" sx={{ fontWeight: "bold", textAlign: "right" }}>
          {round_ex(totalSubtotal, 4)}
        </TableCell>
        <TableCell></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ textAlign: "right", py: 2 }}>
          <AddButton />
        </TableCell>
      </TableRow>
    </>
  );

  const renderMobileView = () => (
    <>
      {!detailRowId && (
        <Typography
          component="div"
          className="mobile-summary"
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            borderWidth: "2px",
            margin: "30px 0px",
          }}
        >
          <TableRow sx={{ display: "flex" }}>
            <TableCell
              sx={{
                fontWeight: "bold",
                textAlign: "left",
                width: "50%",
                py: 1.5,
                borderWidth: "2px",
                borderColor: "grey.200",
              }}
            >
              Sub Qty
            </TableCell>
            <TableCell
              sx={{
                fontWeight: "bold",
                textAlign: "left",
                width: "50%",
                py: 1.5,
                borderWidth: "2px",
                borderColor: "grey.200",
              }}
            >
              Sub Total
            </TableCell>
          </TableRow>
          <TableRow sx={{ display: "flex" }}>
            <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "50%", py: 1.5, border: "none" }}>
              {totalQty}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "left", width: "50%", py: 1.5, border: "none" }}>
              {round_ex(totalSubtotal, 4)}
            </TableCell>
          </TableRow>
        </Typography>
      )}
      <TableRow sx={{ display: "block", mt: 2 }}>
        <TableCell
          colSpan={6}
          sx={{
            textAlign: "right",
            display: "block",
            px: 0,
            py: 2,
            border: "none",
            borderTop: "1px solid",
            borderColor: "primary.main",
            "[data-mui-color-scheme='dark'] &": {
              borderColor: "primary.light",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
            <Typography component="div" sx={{ mr: 2 }}>
              <CloseButton />
            </Typography>

            <AddButton />
          </Box>
        </TableCell>
      </TableRow>
    </>
  );

  const renderBudgetCountyScheduleCells = (row, handleBudgetCountySchedule, isSmall) => {
    const responsiveStyles = {
      cell: (isSmall) => ({ padding: isSmall ? "8px 4px" : "16px 8px", minWidth: isSmall ? "auto" : "150px" }),
      textField: (isSmall) => ({ minWidth: isSmall ? "100%" : "60px", maxWidth: isSmall ? "100%" : "160px" }),
      textFieldName: (isSmall) => ({ minWidth: isSmall ? "100%" : "180px" }),
    };
    const wrapInTableCell = (content) =>
      isSmall ? content : <TableCell sx={responsiveStyles.cell(isSmall)}>{content}</TableCell>;

    return (
      <>
        {wrapInTableCell(
          <FormControl fullWidth className="schedule_field">
            <StatefulControlledTextField
              name="name"
              defaultValue={row.name}
              onChange={(e) => handleBudgetCountySchedule(e, row)}
              isEditable={true}
              fullWidth={true}
              label={isSmall ? "Name" : undefined}
              InputLabelProps={{ shrink: true }}
              sx={responsiveStyles.textFieldName(isSmall)}
            />
          </FormControl>
        )}
        {wrapInTableCell(
          <FormControl fullWidth className="schedule_field">
            <StatefulControlledTextField
              name="quantity"
              defaultValue={row.quantity}
              onChange={(e) => handleBudgetCountySchedule(e, row)}
              isEditable={true}
              fullWidth={true}
              label={isSmall ? "Quantity" : undefined}
              InputLabelProps={{ shrink: true }}
              sx={responsiveStyles.textField(isSmall)}
              maskType={MASK_NUMERIC}
            />
          </FormControl>
        )}
        {wrapInTableCell(
          <FormControl fullWidth className="schedule_field">
            {isSmall && (
              <InputLabel id="unit_of_measure" style={{ marginTop: "5px" }}>
                Unit of Measure
              </InputLabel>
            )}
            <Select
              name="unit_of_measure"
              sx={{ marginBottom: "20px" }}
              value={row.unit_of_measure}
              onChange={(e) => handleBudgetCountySchedule(e, row)}
              label={isSmall ? "Unit of Measure" : undefined}
            >
              {UNIT_OF_MEASURE_NAMES.map((unit, index) => (
                <MenuItem key={index} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {wrapInTableCell(
          <FormControl fullWidth className="schedule_field">
            <StatefulControlledTextField
              name="unit_value"
              defaultValue={round_ex(row.unit_value, 4)}
              onChange={(e) => handleBudgetCountySchedule(e, row)}
              isEditable={true}
              fullWidth={true}
              label={isSmall ? "Unit Value" : undefined}
              InputLabelProps={{ shrink: true }}
              sx={responsiveStyles.textField(isSmall)}
              maskType={MASK_NUMERIC}
            />
          </FormControl>
        )}
      </>
    );
  };

  return (
    <Popover
      open={isPopoverOpen}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorReference={"anchorPosition"}
      anchorPosition={
        isSmall
          ? { top: 24, left: window.innerWidth / 2 }
          : { top: window.innerHeight / 2, left: window.innerWidth / 2 }
      }
      transformOrigin={{ vertical: isSmall ? "top" : "center", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            minWidth: isSmall ? "85vw" : "70vw",
            maxWidth: isMobile ? "92vw" : "95vw",
            p: 0,
            ...(isMobile && {
              left: "auto",
              right: "auto",
              marginLeft: "auto",
              marginRight: "auto",
              transform: "none",
              borderRadius: 4,
            }),
          },
        },
      }}
      sx={{ backgroundColor: "colors.semiTransparentGray" }}
    >
      <Box sx={{ p: 3, backgroundColor: "background.paper", overflowX: isSmall ? "hidden" : "auto" }}>
        <Box sx={{ display: "flex", alignItems: "start", flexDirection: "column", mb: 3 }}>
          <Typography variant={isSmall ? "h6" : "h5"} sx={{ textAlign: "left", flex: 1 }}>
            {selectedField?.name} Schedule
          </Typography>
          {detailRowId ? (
            <Typography
              component="span"
              onClick={() => setDetailRowId(null)}
              sx={{
                mt: 2,
                color: "primary.main",
                "[data-mui-color-scheme='dark'] &": { color: "primary.light" },
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ArrowLeftIcon
                sx={{ color: "primary.main", "[data-mui-color-scheme='dark'] &": { color: "primary.light" } }}
              />
              Back to list
            </Typography>
          ) : null}
          <Box sx={{ width: 86 }} />
        </Box>

        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Table size={isSmall ? "small" : "medium"}>
            <TableHead>
              {!isSmall && (
                <TableRow>
                  {["Name", "Qty", "Unit", "Per", "Subtotal", "Action"].map((text, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "grey.100",
                        textAlign: index > 3 ? "right" : "left",
                        fontSize: "1rem",
                      }}
                    >
                      {text}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {isSkeletonLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", fontWeight: "bold", color: "text.secondary" }}>
                    <SkeletonTable skeletonTableRows={2} skeletonTableColumn={4} />
                  </TableCell>
                </TableRow>
              ) : filteredBudgetSchedule?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", fontWeight: "bold", color: "text.secondary" }}>
                    No records found. Click "Add Item" to create one.
                  </TableCell>
                </TableRow>
              ) : // mobile: if detailRowId is set show only that detail (with slide), otherwise show the list
              isSmall ? (
                detailRowId ? (
                  // only show selected detail
                  (() => {
                    const row = filteredBudgetSchedule.find((r) => r.id === detailRowId);
                    return row ? (
                      <TableRow key={row.id}>
                        <TableCell colSpan={6} sx={{ padding: 0, borderBottom: "none" }}>
                          <Slide in={Boolean(detailRowId)} direction="left" mountOnEnter unmountOnExit>
                            <Box
                              className="detail-view"
                              sx={{
                                p: 1,
                                boxShadow: "0px 4px 14px 0px #00000024",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderWidth: "2px",
                              }}
                            >
                              <Box
                                sx={{
                                  mt: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    display: "flex",
                                    justifyContent: "end",
                                    position: "relative",
                                    bottom: 15,
                                    left: 10,
                                  }}
                                >
                                  {DeleteButton(row)}
                                </Typography>
                                {renderBudgetCountyScheduleCells(row, handleBudgetCountySchedule, isSmall)}
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontWeight: "bold",
                                  padding: "10px 6px",
                                  border: "1px solid",
                                  borderColor: "grey.200",
                                  borderRadius: "6px",
                                  borderWidth: "2px",
                                }}
                              >
                                <span>Total:</span>
                                <span>{round_ex(row.value, 4)}</span>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontWeight: "bold",
                                  alignItems: "center",
                                  mt: 2,
                                }}
                              ></Box>
                            </Box>
                          </Slide>
                        </TableCell>
                      </TableRow>
                    ) : null;
                  })()
                ) : (
                  // list view on mobile
                  filteredBudgetSchedule.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ padding: 0, border: "none" }}>
                        <Accordion
                          disableGutters
                          expanded={false}
                          sx={{
                            width: "100%",
                            marginBottom: "15px !important",
                            backgroundColor: "var(--mui-palette-background-paper) !important",
                          }}
                          className="accordion_container"
                        >
                          <AccordionSummary
                            className="accordion_summary"
                            onClick={() => setDetailRowId(row.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                              <ArrowDownIcon sx={{ mr: 1 }} />
                            </Typography>
                            <Typography sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
                              {row.name}
                            </Typography>
                            <Typography
                              sx={{ marginLeft: "auto", fontWeight: "bold", display: "flex", alignItems: "center" }}
                            >
                              {formatPrice(row.value)}
                            </Typography>
                          </AccordionSummary>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  ))
                )
              ) : (
                // desktop normal rows
                filteredBudgetSchedule.map((row, idx) => (
                  <TableRow
                    data-testid={`schedule-row-${idx}`}
                    key={row.id}
                    sx={newRowIds.includes(row.id) ? { backgroundColor: "colors.backgroundNewRow" } : {}}
                  >
                    {renderBudgetCountyScheduleCells(row, handleBudgetCountySchedule, isSmall)}
                    <TableCell data-testid="schedule-row-subtotal" sx={{ textAlign: "right", paddingBottom: "36px" }}>
                      {round_ex(row.value, 4)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", paddingBottom: "38px" }}>{DeleteButton(row)}</TableCell>
                  </TableRow>
                ))
              )}

              {isSmall ? renderMobileView() : renderDesktopView()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Popover>
  );
};
